import { shallowEqual, useSelector } from 'react-redux'

import {
    makeUpdateListSorter,
    makeFetchListRequest,
    makeDownloadListRequest,
} from 'actions/ui/shared'
import { useDeepCompareMemo } from 'hooks/useDeepCompareMemo'
import { CobaltPath } from 'routing/Path'
import { selectDomainValue } from 'selectors/ui'
import { selectListSettings } from 'selectors/ui/list'
import { List, RootReduxState, Sorter } from 'types'

import { useAction } from './useAction'

interface ListDispatch {
    updateSorter: (sorter: Sorter) => void
    reloadData: (options?: { noCount?: boolean }) => void
    downloadData: () => void
}

export const useListDispatch = (listPath: CobaltPath): ListDispatch => {
    const makeUpdateListSorterForList = useDeepCompareMemo(
        () => makeUpdateListSorter(listPath),
        [listPath]
    )
    const makeFetchListRequestForList = useDeepCompareMemo(
        () => makeFetchListRequest(listPath),
        [listPath]
    )
    const makeDownloadListRequestForList = useDeepCompareMemo(
        () => makeDownloadListRequest(listPath),
        [listPath]
    )

    return {
        updateSorter: useAction<Sorter>(makeUpdateListSorterForList),
        reloadData: useAction<{ noCount?: boolean }>(
            makeFetchListRequestForList
        ),
        downloadData: useAction<{ noCount?: boolean }>(
            makeDownloadListRequestForList
        ),
    }
}

export const useListState = <T>(
    listPath: CobaltPath
): Omit<List<T>, 'listSettings'> & List<T>['listSettings'] => ({
    data: useSelector(
        (state: RootReduxState) =>
            selectDomainValue(state, [...listPath, 'data']),
        shallowEqual
    ),
    loading: useSelector(
        (state: RootReduxState) =>
            selectDomainValue(state, [...listPath, 'loading']),
        shallowEqual
    ),
    error: useSelector(
        (state: RootReduxState) =>
            selectDomainValue(state, [...listPath, 'error']),
        shallowEqual
    ),
    downloading: useSelector(
        (state: RootReduxState) =>
            selectDomainValue(state, [...listPath, 'downloading']),
        shallowEqual
    ),
    sorter: useSelector(
        (state: RootReduxState) => selectListSettings(state, listPath).sorter,
        shallowEqual
    ),
})
